import {
  ArrowUpRight,
  BarChart3,
  CalendarClock,
  CheckSquare,
  Clock,
  Filter,
  Flame,
} from "lucide-react";
import {
  useGetIdentity,
  useGetList,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { Link } from "react-router";

import { formatDateOnly } from "../clients/clientModel";
import { StatusPill } from "../calendar/PieceDetail";
import {
  FORMAT_ICONS,
  PIECE_STATUSES,
  STATUS_COLORS,
  toDateKey,
} from "../calendar/pieceModel";
import { ActivityLog } from "../activity/ActivityLog";
import { Avatar } from "../contacts/Avatar";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Client, Contact, ContentPiece, Deal, Proposal } from "../types";
import { DealsChart } from "./DealsChart";
import { UnassignedPieces } from "./TeamDashboard";
import { AddTask } from "../tasks/AddTask";
import { TasksListContent } from "../tasks/TasksListContent";

// Administrator home: content work across every client plus the commercial
// side (tasks and pipeline) in one place.
export const AgencyDashboard = () => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const { identity } = useGetIdentity();

  const today = new Date();
  const inAWeek = new Date(today);
  inAWeek.setDate(today.getDate() + 7);

  const { total: inReview = 0 } = useGetList<ContentPiece>("content_pieces", {
    pagination: { page: 1, perPage: 1 },
    filter: { status: "in_review" },
  });
  const { total: changesRequested = 0 } = useGetList<ContentPiece>(
    "content_pieces",
    {
      pagination: { page: 1, perPage: 1 },
      filter: { status: "changes_requested" },
    },
  );
  const { data: clients = [], total: activeClients = 0 } = useGetList<Client>(
    "clients",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "name", order: "ASC" },
    },
  );
  const { total: proposalsSent = 0 } = useGetList<Proposal>("proposals", {
    pagination: { page: 1, perPage: 1 },
    filter: { status: "sent" },
  });
  const { data: upcoming = [] } = useGetList<ContentPiece>("content_pieces", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "publish_date", order: "ASC" },
    filter: {
      "publish_date@gte": toDateKey(today),
      "publish_date@lte": toDateKey(inAWeek),
      "status@neq": "published",
    },
  });

  const clientName = (id: unknown) =>
    clients.find((client) => String(client.id) === String(id))?.name ?? "";
  const firstName = String(identity?.fullName ?? "").split(" ")[0];
  const dateLabel = today.toLocaleDateString(
    locale === "es" ? "es-CO" : locale,
    { weekday: "long", day: "numeric", month: "long" },
  );

  return (
    <div className="mt-6 flex flex-col gap-6 pb-8">
      <div>
        <p className="text-sm text-muted-foreground first-letter:uppercase">
          {dateLabel}
        </p>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight">
          {translate("crm.agency_dashboard.greeting", { name: firstName })}
        </h1>
        <p className="mt-1 text-muted-foreground">
          {translate("crm.agency_dashboard.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        <StatLink
          to="/calendar?status=in_review"
          label={translate("crm.agency_dashboard.in_review")}
          value={inReview}
          color={STATUS_COLORS.in_review}
          highlight
        />
        <StatLink
          to="/calendar?status=changes_requested"
          label={translate("crm.agency_dashboard.changes_requested")}
          value={changesRequested}
          color={STATUS_COLORS.changes_requested}
        />
        <StatLink
          to="/clients"
          label={translate("crm.agency_dashboard.active_clients")}
          value={activeClients}
          color="var(--muted-foreground)"
        />
        <StatLink
          to="/proposals"
          label={translate("crm.agency_dashboard.proposals_sent")}
          value={proposalsSent}
          color="#2f6bff"
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-4">
          <DealsChart />
          <ContentMonthChart />
          <section className="rounded-[22px] bg-card p-5">
            <h2 className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <CalendarClock className="size-4" />
              {translate("crm.agency_dashboard.upcoming")}
            </h2>
            {upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {translate("crm.agency_dashboard.upcoming_empty")}
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {upcoming.map((piece) => {
                  const Icon = FORMAT_ICONS[piece.format];
                  return (
                    <li key={piece.id}>
                      <Link
                        to={`/calendar?client=${piece.company_id}&month=${piece.publish_date?.slice(0, 7)}&piece=${piece.id}`}
                        className="flex items-center gap-3 rounded-2xl bg-secondary px-4 py-3 text-foreground no-underline hover:bg-accent"
                      >
                        <Icon className="size-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">
                            {piece.title}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            {clientName(piece.company_id)}
                            {piece.publish_date
                              ? ` · ${formatDateOnly(piece.publish_date, locale)}`
                              : ""}
                          </span>
                        </span>
                        <StatusPill status={piece.status} />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
          <section className="rounded-[22px] bg-card p-5">
            <h2 className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Clock className="size-4" />
              {translate("crm.dashboard.latest_activity")}
            </h2>
            <ActivityLog pageSize={10} />
          </section>
        </div>

        <div className="flex flex-col gap-4">
          <PipelineSummary />
          <section className="rounded-[22px] bg-card p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <CheckSquare className="size-4" />
                {translate("crm.dashboard.upcoming_tasks")}
              </h2>
              <AddTask display="icon" selectContact />
            </div>
            <TasksListContent />
          </section>
          <UnassignedPieces />
          <HotContactsSection />
        </div>
      </div>
    </div>
  );
};

const StatLink = ({
  to,
  label,
  value,
  color,
  highlight = false,
}: {
  to: string;
  label: string;
  value: number;
  color: string;
  highlight?: boolean;
}) => (
  <Link
    to={to}
    className={`relative rounded-[22px] p-5 no-underline transition-colors ${
      highlight
        ? "bg-primary text-primary-foreground"
        : "bg-card text-card-foreground hover:bg-accent"
    }`}
  >
    <span className="flex items-start gap-2 pr-8 text-sm leading-snug opacity-80">
      <span
        className="mt-1.5 size-2 shrink-0 rounded-full"
        style={{ background: highlight ? "#fff" : color }}
      />
      {label}
    </span>
    <span className="mt-3 block text-4xl font-semibold leading-none tracking-tight">
      {value}
    </span>
    <span
      className={`absolute -top-1.5 -right-1.5 grid size-11 place-items-center rounded-full border-[6px] border-background ${
        highlight ? "bg-white text-primary" : "bg-card text-muted-foreground"
      }`}
    >
      <ArrowUpRight className="size-4" />
    </span>
  </Link>
);

const PipelineSummary = () => {
  const translate = useTranslate();
  const { dealStages, dealPipelineStatuses } = useConfigurationContext();
  const { data: deals = [] } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 1000 },
    filter: { "stage@neq": "lost" },
  });
  const activeDeals = deals.filter((deal) => !deal.archived_at);
  const openStages = dealStages.filter(
    (stage) =>
      stage.value !== "lost" && !dealPipelineStatuses.includes(stage.value),
  );
  const counts = openStages.map((stage) => ({
    ...stage,
    count: activeDeals.filter((deal) => deal.stage === stage.value).length,
  }));
  const max = Math.max(1, ...counts.map((stage) => stage.count));
  const openTotal = counts.reduce((sum, stage) => sum + stage.count, 0);

  return (
    <section className="rounded-[22px] bg-card p-5">
      <Link
        to="/deals"
        className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground no-underline hover:text-foreground"
      >
        <Filter className="size-4" />
        {translate("crm.agency_dashboard.pipeline")}
      </Link>
      {openTotal === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.agency_dashboard.pipeline_empty")}
        </p>
      ) : (
        <>
          <p className="mb-3 text-sm text-muted-foreground">
            {translate("crm.agency_dashboard.open_deals", {
              smart_count: openTotal,
            })}
          </p>
          <ul className="flex flex-col gap-2.5">
            {counts.map((stage) => (
              <li key={stage.value} className="text-sm">
                <div className="mb-1 flex justify-between">
                  <span>{stage.label}</span>
                  <span className="font-semibold">{stage.count}</span>
                </div>
                <div className="h-1.5 rounded-full bg-secondary">
                  <div
                    className="h-1.5 rounded-full bg-primary"
                    style={{ width: `${(stage.count / max) * 100}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
};

// Pieces of the current month by status, across every client.
const ContentMonthChart = () => {
  const translate = useTranslate();
  const today = new Date();
  const from = toDateKey(new Date(today.getFullYear(), today.getMonth(), 1));
  const to = toDateKey(new Date(today.getFullYear(), today.getMonth() + 1, 0));
  const { data: pieces = [] } = useGetList<ContentPiece>("content_pieces", {
    pagination: { page: 1, perPage: 1000 },
    filter: { "publish_date@gte": from, "publish_date@lte": to },
  });
  const counts = PIECE_STATUSES.map((status) => ({
    status,
    count: pieces.filter((piece) => piece.status === status).length,
  }));
  const max = Math.max(1, ...counts.map((item) => item.count));

  return (
    <section className="rounded-[22px] bg-card p-5">
      <Link
        to="/calendar"
        className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground no-underline hover:text-foreground"
      >
        <BarChart3 className="size-4" />
        {translate("crm.agency_dashboard.content_month", {
          smart_count: pieces.length,
        })}
      </Link>
      <div className="flex h-44 items-end gap-3">
        {counts.map(({ status, count }) => (
          <Link
            key={status}
            to={`/calendar?status=${status}`}
            className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-2 no-underline"
          >
            <span className="text-lg font-semibold text-foreground">
              {count}
            </span>
            <span
              className="w-full max-w-16 rounded-t-xl"
              style={{
                height: `${Math.max(4, (count / max) * 100)}%`,
                background: STATUS_COLORS[status],
                opacity: count === 0 ? 0.25 : 1,
              }}
            />
            <span className="w-full truncate text-center text-[11px] text-muted-foreground">
              {translate(`resources.content_pieces.statuses.${status}`)}
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
};

// Contacts marked as hot in their latest note, for every administrator.
const HotContactsSection = () => {
  const translate = useTranslate();
  const { data: contacts = [] } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 8 },
    sort: { field: "last_seen", order: "DESC" },
    filter: { status: "hot" },
  });

  return (
    <section className="rounded-[22px] bg-card p-5">
      <h2 className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Flame className="size-4" />
        {translate("resources.contacts.hot.title")}
      </h2>
      {contacts.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.contacts.hot.empty_hint")}
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {contacts.map((contact) => (
            <li key={contact.id}>
              <Link
                to={`/contacts/${contact.id}/show`}
                className="flex items-center gap-3 rounded-xl px-2 py-2 text-foreground no-underline hover:bg-accent"
              >
                <Avatar record={contact} width={25} />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">
                    {contact.first_name} {contact.last_name}
                  </span>
                  {contact.title ? (
                    <span className="block truncate text-xs text-muted-foreground">
                      {contact.title}
                    </span>
                  ) : null}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
